import type { IntegrationName } from "@appsmith/carbon-connect";
import type { ReduxAction } from "actions/ReduxActionTypes";
import {
  ReduxActionErrorTypes,
  ReduxActionTypes,
} from "ee/constants/ReduxActionConstants";
import { createImmerReducer } from "utils/ReducerUtils";

export type IntegrationType = "function-calling" | "rag";

export interface AiAgentReduxState {
  // on click of integration button in carbon that are function-calling based, we want to start a flow
  // for creating datasource and queries, this prop tells which integration is being created
  installingIntegrationName: IntegrationName | null;
  // This prop tells if the carbon modal is open or not. Why can't we put it in the state of the CarbonButton?
  // because the modal can be opened from outside of carbon CarbonButton.
  isCarbonModalOpen: boolean;
  // Used in carbon modal to show integrations of a specific type.
  selectedIntegrationType: IntegrationType | null;
  isSupportModalOpen: boolean;
  isCreateAgentModalOpen: boolean;
  isNewUserFlowEnabled: boolean;
  // This prop tells if the user is creating an agent app or not.
  // Mainly used to show the correct reconnent datasource modal content when the user is creating an agent app.
  isCreatingAgent: boolean;
}

const initialState: AiAgentReduxState = {
  installingIntegrationName: null,
  isCarbonModalOpen: false,
  isSupportModalOpen: false,
  isCreateAgentModalOpen: false,
  isNewUserFlowEnabled: false,
  selectedIntegrationType: null,
  isCreatingAgent: false,
};

export const handlers = {
  [ReduxActionTypes.CREATE_DATASOURCE_AND_SEARCH_QUERIES_FOR_AI_AGENT_INIT]: (
    draftState: AiAgentReduxState,
    action: ReduxAction<{ integrationName: IntegrationName }>,
  ) => {
    draftState.installingIntegrationName = action.payload.integrationName;
  },
  [ReduxActionTypes.CREATE_DATASOURCE_AND_SEARCH_QUERIES_FOR_AI_AGENT_SUCCESS]:
    (draftState: AiAgentReduxState) => {
      draftState.installingIntegrationName = null;
    },
  [ReduxActionTypes.OPEN_CARBON_MODAL]: (
    draftState: AiAgentReduxState,
    action: ReduxAction<{
      shouldOpen: boolean;
      integrationType: IntegrationType | null;
    }>,
  ) => {
    draftState.isCarbonModalOpen = action.payload.shouldOpen;
    draftState.selectedIntegrationType = action.payload.integrationType;

    if (action.payload.shouldOpen === false) {
      draftState.selectedIntegrationType = null;
    }
  },
  [ReduxActionErrorTypes.CREATE_DATASOURCE_AND_SEARCH_QUERIES_FOR_AI_AGENT_ERROR]:
    (draftState: AiAgentReduxState) => {
      draftState.installingIntegrationName = null;
    },
  [ReduxActionTypes.INSTALLING_INTEGRATION_NAME]: (
    draftState: AiAgentReduxState,
    action: ReduxAction<{ integrationName: IntegrationName }>,
  ) => {
    draftState.installingIntegrationName = action.payload.integrationName;
  },
  [ReduxActionTypes.TOGGLE_AI_SUPPORT_MODAL]: (
    draftState: AiAgentReduxState,
  ) => {
    draftState.isSupportModalOpen = !draftState.isSupportModalOpen;
  },
  [ReduxActionTypes.SET_CREATE_AGENT_MODAL_OPEN]: (
    draftState: AiAgentReduxState,
    action: ReduxAction<{ isOpen: boolean }>,
  ) => {
    draftState.isCreateAgentModalOpen = action.payload.isOpen;
  },
  [ReduxActionTypes.SET_NEW_USER_FLOW_FOR_AGENT]: (
    draftState: AiAgentReduxState,
    action: ReduxAction<{ isEnabled: boolean }>,
  ) => {
    draftState.isNewUserFlowEnabled = action.payload.isEnabled;
    draftState.isCreateAgentModalOpen = true;
  },
  [ReduxActionTypes.SET_IS_CREATING_AGENT]: (
    draftState: AiAgentReduxState,
    action: ReduxAction<{ isCreatingAgent: boolean }>,
  ) => {
    draftState.isCreatingAgent = action.payload.isCreatingAgent;
  },
};

const aiAgentReducer = createImmerReducer(initialState, handlers);

export { aiAgentReducer };
